/**
 * Cross-Tab-Signal gegen einen zweiten Parallel-Lauf auf demselben Feed
 * (Konvergenz 4, Befund 1).
 *
 * Problem: die Parallel-Sperre der Optimierung ist tab-lokaler React-State.
 * Ein zweiter Tab desselben Browsers erfaehrt von einem laufenden Job nichts
 * (optimization_jobs ist bewusst nicht realtime-publiziert, das Polling stoppt
 * bei data=null, refetchOnWindowFocus ist global aus) — sein Klick passiert
 * den lokalen Guard und der Server laesst bis zu 3 parallele Jobs pro USER zu.
 * Ergebnis: derselbe Worst-N-Lauf doppelt, doppelt verbranntes
 * Lifetime-Kontingent, und "Abbrechen" purgt die Queue des jeweils anderen.
 *
 * Mechanik: beim Lauf-Start (nach Server-Annahme) schreibt der startende Tab
 * einen Marker in localStorage; beim Terminal-Status wird er entfernt. Andere
 * Tabs desselben Browsers sehen den Marker synchron (Klick-Guard) und werden
 * per storage-Event geweckt, damit sie den Lauf UEBERNEHMEN (Query
 * invalidieren, Polling starten) statt nur zu sperren.
 *
 * EHRLICHE GRENZE: das deckt ausschliesslich Tabs DESSELBEN Browsers.
 * Ein zweiter Browser, ein anderes Geraet oder ein Inkognito-Fenster sehen
 * den Marker nicht. Der eigentliche Schutz — ein serverseitiger Per-Feed-Guard
 * in enqueue-optimization-batches (409 JOB_ALREADY_RUNNING) — steht noch aus
 * und ist eine bewusst offene Entscheidung fuer Dennis.
 *
 * TTL: der Stuck-Job-Watchdog (check-job-failures) failt stale Jobs nach
 * 10 Minuten. Ein verwaister Marker (Tab geschlossen, bevor der Lauf terminal
 * wurde) darf den Feed deshalb nicht laenger als ~15 Minuten sperren.
 */

export interface OptimizationRunMarker {
  jobId: string;
  ts: number;
}

export const OPTIMIZATION_RUN_MARKER_TTL_MS = 15 * 60 * 1000;

const KEY_PREFIX = 'fo.optimizationRun.';

const keyFor = (feedId: string) => `${KEY_PREFIX}${feedId}`;

/** Alle Zugriffe sind Best-Effort: localStorage kann fehlen oder voll sein
 *  (Safari Private Mode, Quota). Ein fehlender Marker darf nie einen Lauf
 *  verhindern — er macht das Signal nur wirkungslos. */
export function markOptimizationRunning(feedId: string, jobId: string): void {
  try {
    localStorage.setItem(keyFor(feedId), JSON.stringify({ jobId, ts: Date.now() }));
  } catch {
    /* best-effort */
  }
}

export function getOptimizationRunMarker(feedId: string): OptimizationRunMarker | null {
  try {
    const raw = localStorage.getItem(keyFor(feedId));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<OptimizationRunMarker> | null;
    if (typeof parsed?.jobId !== 'string' || typeof parsed?.ts !== 'number') {
      localStorage.removeItem(keyFor(feedId));
      return null;
    }
    if (Date.now() - parsed.ts > OPTIMIZATION_RUN_MARKER_TTL_MS) {
      // Verwaister Marker: der startende Tab ist weg, der Watchdog hat den
      // Job laengst gefailt. Aufraeumen statt dauerhaft sperren.
      localStorage.removeItem(keyFor(feedId));
      return null;
    }
    return { jobId: parsed.jobId, ts: parsed.ts };
  } catch {
    return null;
  }
}

/**
 * Entfernt den Marker eines Feeds. Mit `jobId` wird nur der Marker GENAU
 * dieses Laufs entfernt — ein Tab, der einen alten Lauf abraeumt, darf den
 * Marker eines inzwischen gestarteten neuen Laufs nicht mitnehmen.
 */
export function clearOptimizationRunMarker(feedId: string, jobId?: string): void {
  try {
    if (jobId) {
      const raw = localStorage.getItem(keyFor(feedId));
      if (raw) {
        try {
          const parsed = JSON.parse(raw) as Partial<OptimizationRunMarker> | null;
          if (typeof parsed?.jobId === 'string' && parsed.jobId !== jobId) return;
        } catch {
          /* kaputter Marker: entfernen ist richtig */
        }
      }
    }
    localStorage.removeItem(keyFor(feedId));
  } catch {
    /* best-effort */
  }
}

/**
 * storage-Events feuern nur in ANDEREN Tabs desselben Browsers — genau die
 * Zielgruppe. Der Callback bekommt den frischen Marker (oder null nach
 * Entfernen/TTL-Ablauf), der Aufrufer entscheidet, ob er den Lauf uebernimmt.
 */
export function subscribeToOptimizationRunMarker(
  feedId: string,
  onChange: (marker: OptimizationRunMarker | null) => void,
): () => void {
  const key = keyFor(feedId);
  const handler = (evt: StorageEvent) => {
    if (evt.key !== key) return;
    onChange(getOptimizationRunMarker(feedId));
  };
  window.addEventListener('storage', handler);
  return () => window.removeEventListener('storage', handler);
}
